import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminDatHostBudget.css';

/**
 * Selectable time ranges, matching the Django `range` parameter.
 * @type {Array<string>}
 */
const RANGES = ['24h', '7d', '30d', '90d', '12m'];

/**
 * Selectable bucket sizes. `hour` is only valid for the two short ranges —
 * Django builds it from raw samples, which are pruned after the retention.
 * @type {Array<string>}
 */
const BUCKETS = ['hour', 'day', 'week', 'month'];

/**
 * Ranges that allow the hourly bucket.
 * @type {Array<string>}
 */
const HOUR_RANGES = ['24h', '7d'];

/**
 * Default bucket per range, picked when the range changes.
 * @type {Object<string, string>}
 */
const DEFAULT_BUCKET = { '24h': 'hour', '7d': 'day', '30d': 'day', '90d': 'week', '12m': 'month' };

/**
 * Cost causes in fixed stacking order. The order is also the palette order —
 * it was validated as adjacent pairs (see AdminDatHostBudget.css), so it must
 * not be re-sorted by size.
 * @type {Array<string>}
 */
const CAUSES = ['match', 'keepalive', 'manual', 'idle', 'unknown'];

/**
 * Match kinds as reported by the API, in display order.
 * @type {Array<string>}
 */
const KINDS = ['5v5', 'tournament', 'league', 'wingman', 'duel'];

/**
 * CSS custom properties holding the chart colors. Read at runtime so the
 * charts follow the active theme's polarity (recharts needs literal colors,
 * not `var(...)`, in its SVG attributes).
 * @type {Object<string, string>}
 */
const COLOR_VARS = {
  match: '--dhb-cause-match',
  keepalive: '--dhb-cause-keepalive',
  manual: '--dhb-cause-manual',
  idle: '--dhb-cause-idle',
  unknown: '--dhb-cause-unknown',
  series: '--dhb-series',
  budget: '--dhb-budget',
  surface: '--dhb-surface',
};

/**
 * Number fields of the settings form: config key, input step and minimum.
 * Minimums mirror the server-side validation in billing_service.CONFIG_FIELDS.
 * @type {Array<{key: string, step: string, min: string}>}
 */
const NUMBER_FIELDS = [
  { key: 'monthly_budget_eur', step: '0.5', min: '0' },
  { key: 'low_credits_eur', step: '0.5', min: '0' },
  { key: 'low_credits_remind_hours', step: '1', min: '1' },
  { key: 'idle_alert_minutes', step: '1', min: '1' },
  { key: 'poll_interval_seconds', step: '30', min: '60' },
  { key: 'raw_retention_days', step: '1', min: '1' },
];

/**
 * Reads the chart colors from the component's CSS variables.
 * @param {HTMLElement|null} el - The element the variables are defined on.
 * @returns {Object<string, string>} Color per COLOR_VARS key ('' if unreadable).
 */
function readColors(el) {
  if (!el) return {};
  const style = getComputedStyle(el);
  return Object.fromEntries(
    Object.entries(COLOR_VARS).map(([key, name]) => [key, style.getPropertyValue(name).trim()]),
  );
}

/**
 * Keeps chart colors in sync with the theme. Re-reads whenever `<html>`
 * changes `data-theme`/`data-polarity`, so switching theme repaints the charts
 * without a reload.
 * @param {React.RefObject<HTMLElement>} ref - Element carrying the variables.
 * @param {boolean} ready - Whether the element is mounted yet.
 * @returns {Object<string, string>} Current colors.
 */
function useThemeColors(ref, ready) {
  const [colors, setColors] = useState({});
  useEffect(() => {
    if (!ready) return undefined;
    setColors(readColors(ref.current));
    const observer = new MutationObserver(() => setColors(readColors(ref.current)));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-polarity'],
    });
    return () => observer.disconnect();
  }, [ref, ready]);
  return colors;
}

/**
 * Formats a euro amount for the current locale.
 * @param {number|null|undefined} value - Amount in EUR.
 * @param {number} [digits=2] - Fraction digits.
 * @returns {string} Formatted amount, or "—" when missing.
 */
function formatEur(value, digits = 2) {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString(currentLocale(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Formats a bucket start (ISO date or datetime) as an axis label.
 * @param {string} iso - Bucket start from the API.
 * @param {string} bucket - Bucket size (hour/day/week/month).
 * @returns {string} Short, locale-aware label.
 */
function formatBucket(iso, bucket) {
  const locale = currentLocale();
  if (bucket === 'hour') {
    return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  // Datum utan tid tolkas som UTC av Date — lägg på mitt på dagen så att
  // tidszonen aldrig flyttar etiketten till dagen före.
  const date = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (bucket === 'month') {
    return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
  }
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/**
 * Formats an ISO timestamp as local date + time.
 * @param {string|null} iso - Timestamp.
 * @returns {string} Formatted value, or "—".
 */
function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(currentLocale());
}

/**
 * One headline figure.
 * @param {Object} props
 * @param {string} props.label - What the figure measures.
 * @param {string} props.value - The figure.
 * @param {string} [props.hint] - Clarifying sub-line.
 * @param {string} [props.tone] - Optional state: "warn" or "danger".
 */
function StatTile({ label, value, hint, tone }) {
  return (
    <div className={`dhb-tile${tone ? ` dhb-tile-${tone}` : ''}`}>
      <span className="dhb-tile-label">{label}</span>
      <span className="dhb-tile-value">{value}</span>
      {hint && <span className="dhb-tile-hint">{hint}</span>}
    </div>
  );
}

/**
 * A titled dashboard panel.
 * @param {Object} props
 * @param {string} props.title - Panel heading.
 * @param {string} [props.desc] - Explanatory line under the heading.
 * @param {boolean} [props.wide] - Span the full grid width.
 * @param {React.ReactNode} props.children - Panel body.
 */
function Panel({ title, desc, wide, children }) {
  return (
    <section className={`dhb-panel${wide ? ' dhb-panel-wide' : ''}`}>
      <h3 className="dhb-panel-title">{title}</h3>
      {desc && <p className="dhb-panel-desc">{desc}</p>}
      {children}
    </section>
  );
}

/**
 * Horizontal single-series bar list (cost per server / per match kind).
 * @param {Object} props
 * @param {Array<{name: string, total: number}>} props.rows - Bars, largest first.
 * @param {string} props.color - Bar color.
 * @param {string} props.seriesName - Tooltip label for the value.
 * @param {string} props.emptyText - Shown when there are no rows.
 */
function BarList({ rows, color, seriesName, emptyText }) {
  if (rows.length === 0) return <p className="dhb-empty">{emptyText}</p>;
  return (
    <ResponsiveContainer width="100%" height={32 + rows.length * 30}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 0 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => formatEur(v)} contentStyle={{ fontSize: 12 }} />
        <Bar
          dataKey="total"
          name={seriesName}
          fill={color}
          radius={[0, 4, 4, 0]}
          barSize={16}
          label={{ position: 'right', fontSize: 11, formatter: (v) => formatEur(v) }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Admin view: DatHost budget — balance, what the servers cost per cause
 * (match / GSLT keepalive / manual start / idle), the month against the
 * budget, the most expensive matches, invoices, and every setting editable
 * live.
 *
 * Data comes from the poller in Django (servers/billing_service.py) via the
 * FastAPI proxy `/api/admin/dathost-billing/*`; gated on `admin.configure`.
 */
export default function AdminDatHostBudget() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const rootRef = useRef(null);

  const [range, setRange] = useState('30d');
  const [bucket, setBucket] = useState('day');
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [monthSeries, setMonthSeries] = useState(null);
  const [matches, setMatches] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');

  const colors = useThemeColors(rootRef, !loading);

  /**
   * GET one billing endpoint through the proxy.
   * @param {string} path - Endpoint path after /dathost-billing/.
   * @returns {Promise<Object>} Parsed JSON.
   */
  const getJson = useCallback(async (path) => {
    const res = await fetch(`${API_URL}/api/admin/dathost-billing/${path}`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401 || res.status === 403) throw new Error(t('admin.noAccess'));
    if (!res.ok) throw new Error(t('admin.errorStatus', { status: res.status }));
    return res.json();
  }, [getAuthHeaders, t]);

  const loadStatic = useCallback(async () => {
    try {
      const [ov, month, inv, cfg] = await Promise.all([
        getJson('overview'),
        getJson('timeseries?range=30d&bucket=day'),
        getJson('invoices'),
        getJson('config'),
      ]);
      setOverview(ov);
      setMonthSeries(month);
      setInvoices(inv);
      setForm({ ...cfg, budget_alert_pcts: (cfg.budget_alert_pcts || []).join(',') });
      setError('');
    } catch (e) {
      setError(e.message || t('settings.networkError'));
    }
  }, [getJson, t]);

  const loadRange = useCallback(async () => {
    try {
      const [ts, top] = await Promise.all([
        getJson(`timeseries?range=${range}&bucket=${bucket}`),
        getJson(`matches?range=${range}&limit=10`),
      ]);
      setSeries(ts);
      setMatches(top.matches || []);
    } catch (e) {
      setError(e.message || t('settings.networkError'));
    }
  }, [getJson, range, bucket, t]);

  useEffect(() => {
    loadStatic().finally(() => setLoading(false));
  }, [loadStatic]);

  useEffect(() => {
    loadRange();
  }, [loadRange]);

  /**
   * Switches range and falls back to a valid bucket for it.
   * @param {string} next - New range.
   */
  const changeRange = (next) => {
    setRange(next);
    setBucket(DEFAULT_BUCKET[next]);
  };

  const costRows = useMemo(() => {
    if (!series) return [];
    return series.buckets.map((b) => ({
      label: formatBucket(b.start, series.bucket),
      total: b.total,
      ...Object.fromEntries(CAUSES.map((c) => [c, b.by_cause[c] || 0])),
    }));
  }, [series]);

  const causeTotals = useMemo(() => {
    const totals = Object.fromEntries(CAUSES.map((c) => [c, 0]));
    (series?.buckets || []).forEach((b) => {
      CAUSES.forEach((c) => { totals[c] += b.by_cause[c] || 0; });
    });
    return totals;
  }, [series]);

  const kindRows = useMemo(() => {
    const totals = {};
    (series?.buckets || []).forEach((b) => {
      Object.entries(b.by_kind).forEach(([kind, v]) => { totals[kind] = (totals[kind] || 0) + v; });
    });
    return KINDS.filter((k) => totals[k] > 0)
      .map((k) => ({ name: t(`admin.dathostBudget.kind.${k}`), total: totals[k] }))
      .sort((a, b) => b.total - a.total);
  }, [series, t]);

  const serverRows = useMemo(
    () => (series?.by_server || []).filter((s) => s.total > 0)
      .map((s) => ({ name: s.name, total: s.total })),
    [series],
  );

  // Månadens förbrukning ackumulerat + linjär prognos till månadsslutet.
  const monthRows = useMemo(() => {
    if (!monthSeries || !overview) return [];
    const start = overview.month.start;
    const today = new Date();
    const year = Number(start.slice(0, 4));
    const month = Number(start.slice(5, 7));
    const daysInMonth = new Date(year, month, 0).getDate();
    const byDay = Object.fromEntries(
      monthSeries.buckets.filter((b) => b.start >= start).map((b) => [Number(b.start.slice(8, 10)), b.total]),
    );
    const todayDay = today.getDate();
    const rate = (overview.month.to_date || 0) / todayDay;
    let cumulative = 0;
    const rows = [];
    for (let day = 1; day <= daysInMonth; day += 1) {
      const row = { label: String(day) };
      if (day <= todayDay) {
        cumulative += byDay[day] || 0;
        row.actual = Number(cumulative.toFixed(4));
      }
      if (day >= todayDay) {
        row.forecast = Number((cumulative + rate * (day - todayDay)).toFixed(4));
      }
      rows.push(row);
    }
    return rows;
  }, [monthSeries, overview]);

  const balanceRows = useMemo(() => {
    if (!series) return [];
    const raw = HOUR_RANGES.includes(series.range);
    return series.balance.map((p) => ({
      label: raw ? formatDateTime(p.t) : formatBucket(p.t, 'day'),
      credits: p.credits,
    }));
  }, [series]);

  // Insättningar som markörer — bara i dygnsvyn, där x-värdet är ett datum.
  const depositLabels = useMemo(() => {
    if (!series || HOUR_RANGES.includes(series.range) || !invoices) return [];
    const labels = new Set(balanceRows.map((r) => r.label));
    return invoices.invoices
      .filter((i) => i.is_deposit && !i.refunded)
      .map((i) => formatBucket(i.at, 'day'))
      .filter((label) => labels.has(label));
  }, [series, invoices, balanceRows]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setNotice('');
    try {
      const res = await fetch(`${API_URL}/api/admin/dathost-billing/config`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: form.enabled,
          alerts_enabled: form.alerts_enabled,
          cost_source: form.cost_source,
          budget_alert_pcts: form.budget_alert_pcts,
          ...Object.fromEntries(NUMBER_FIELDS.map((f) => [f.key, form[f.key]])),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveError(data.error || data.detail || t('settings.saveError'));
        return;
      }
      setForm({ ...data, budget_alert_pcts: (data.budget_alert_pcts || []).join(',') });
      setNotice(t('admin.dathostBudget.saved'));
      // Budget/källa påverkar siffrorna — ladda om allt.
      loadStatic();
      loadRange();
    } catch {
      setSaveError(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="dhb-empty">{t('live.loading')}</div>;
  if (error && !overview) return <div className="dhb-error">{error}</div>;
  if (!overview) return null;

  const { month } = overview;
  const lowCredits = form && overview.credits !== null && overview.credits < Number(form.low_credits_eur);
  const overBudget = month.forecast_pct !== null && month.forecast_pct >= 100;
  const missingAutostop = overview.servers.filter((s) => !s.autostop);
  const poll = overview.poll || {};

  return (
    <div className="dhb" ref={rootRef}>
      <div className="dhb-header">
        <h1 className="admin-section-title">{t('admin.dathostBudget.title')}</h1>
        <span className="dhb-poll">
          {poll.last_error
            ? t('admin.dathostBudget.pollError', { error: poll.last_error })
            : t('admin.dathostBudget.lastPoll', { at: formatDateTime(poll.last_poll_at) })}
        </span>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {missingAutostop.length > 0 && (
        <div className="dhb-warning">
          {t('admin.dathostBudget.autostopWarning', {
            servers: missingAutostop.map((s) => s.name).join(', '),
          })}
        </div>
      )}

      <div className="dhb-tiles">
        <StatTile
          label={t('admin.dathostBudget.credits')}
          value={formatEur(overview.credits)}
          hint={t('admin.dathostBudget.creditsHint', { at: formatDateTime(overview.credits_at) })}
          tone={lowCredits ? 'danger' : undefined}
        />
        <StatTile
          label={t('admin.dathostBudget.burn30')}
          value={formatEur(overview.burn_30d)}
          hint={t('admin.dathostBudget.burn30Hint', {
            avg: formatEur(overview.avg_daily),
            days: overview.days_covered,
          })}
        />
        <StatTile
          label={t('admin.dathostBudget.daysLeft')}
          value={overview.days_left === null ? '∞' : String(Math.floor(overview.days_left))}
          hint={t('admin.dathostBudget.daysLeftHint')}
          tone={overview.days_left !== null && overview.days_left < 14 ? 'warn' : undefined}
        />
        <StatTile
          label={t('admin.dathostBudget.monthToDate')}
          value={formatEur(month.to_date)}
          hint={t('admin.dathostBudget.monthHint', {
            budget: formatEur(month.budget, 0),
            pct: month.pct_of_budget ?? '—',
            forecast: formatEur(month.forecast),
          })}
          tone={overBudget ? 'warn' : undefined}
        />
      </div>

      <div className="dhb-filters">
        <div className="dhb-segment" role="group" aria-label={t('admin.dathostBudget.range')}>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className={`dhb-segment-btn${r === range ? ' active' : ''}`}
              onClick={() => changeRange(r)}
            >
              {t(`admin.dathostBudget.ranges.${r}`)}
            </button>
          ))}
        </div>
        <div className="dhb-segment" role="group" aria-label={t('admin.dathostBudget.bucket')}>
          {BUCKETS.map((b) => (
            <button
              key={b}
              type="button"
              className={`dhb-segment-btn${b === bucket ? ' active' : ''}`}
              onClick={() => setBucket(b)}
              disabled={b === 'hour' && !HOUR_RANGES.includes(range)}
            >
              {t(`admin.dathostBudget.buckets.${b}`)}
            </button>
          ))}
        </div>
        <span className="dhb-source">
          {t('admin.dathostBudget.sourceNote', {
            source: t(`admin.dathostBudget.sources.${overview.cost_source}`),
          })}
        </span>
      </div>

      <div className="dhb-grid">
        <Panel
          title={t('admin.dathostBudget.costTitle')}
          desc={t('admin.dathostBudget.costDesc')}
          wide
        >
          {costRows.every((r) => r.total === 0) ? (
            <p className="dhb-empty">{t('admin.dathostBudget.noCost')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={costRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeOpacity={0.12} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickMargin={8} minTickGap={12} />
                <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(v) => formatEur(v)} />
                <Tooltip formatter={(v) => formatEur(v, 4)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {CAUSES.map((c) => (
                  <Bar
                    key={c}
                    dataKey={c}
                    name={t(`admin.dathostBudget.cause.${c}`)}
                    stackId="cost"
                    fill={colors[c]}
                    stroke={colors.surface}
                    strokeWidth={1}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
          <table className="dhb-table dhb-cause-table">
            <tbody>
              {CAUSES.map((c) => (
                <tr key={c}>
                  <td>
                    <span className={`dhb-swatch dhb-swatch-${c}`} aria-hidden="true" />
                    {t(`admin.dathostBudget.cause.${c}`)}
                  </td>
                  <td className="dhb-num">{formatEur(causeTotals[c])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel title={t('admin.dathostBudget.monthTitle')} desc={t('admin.dathostBudget.monthDesc')}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeOpacity={0.12} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={8} />
              <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(v) => formatEur(v)} />
              <Tooltip formatter={(v) => formatEur(v)} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {month.budget > 0 && (
                <ReferenceLine
                  y={month.budget}
                  stroke={colors.budget}
                  strokeDasharray="4 4"
                  label={{ value: t('admin.dathostBudget.budgetLine'), position: 'insideTopRight', fontSize: 11 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="actual"
                name={t('admin.dathostBudget.actual')}
                stroke={colors.series}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="forecast"
                name={t('admin.dathostBudget.forecast')}
                stroke={colors.series}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title={t('admin.dathostBudget.balanceTitle')} desc={t('admin.dathostBudget.balanceDesc')}>
          {balanceRows.length === 0 ? (
            <p className="dhb-empty">{t('admin.dathostBudget.noData')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={balanceRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeOpacity={0.12} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(v) => formatEur(v)} />
                <Tooltip formatter={(v) => formatEur(v)} contentStyle={{ fontSize: 12 }} />
                {form && (
                  <ReferenceLine
                    y={Number(form.low_credits_eur)}
                    stroke={colors.budget}
                    strokeDasharray="4 4"
                    label={{ value: t('admin.dathostBudget.lowLine'), position: 'insideBottomRight', fontSize: 11 }}
                  />
                )}
                {depositLabels.map((label) => (
                  <ReferenceLine
                    key={label}
                    x={label}
                    stroke={colors.budget}
                    label={{ value: t('admin.dathostBudget.deposit'), position: 'top', fontSize: 10 }}
                  />
                ))}
                <Line
                  type="stepAfter"
                  dataKey="credits"
                  name={t('admin.dathostBudget.credits')}
                  stroke={colors.series}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title={t('admin.dathostBudget.serverTitle')}>
          <BarList
            rows={serverRows}
            color={colors.series}
            seriesName={t('admin.dathostBudget.cost')}
            emptyText={t('admin.dathostBudget.noCost')}
          />
        </Panel>

        <Panel title={t('admin.dathostBudget.kindTitle')} desc={t('admin.dathostBudget.kindDesc')}>
          <BarList
            rows={kindRows}
            color={colors.match}
            seriesName={t('admin.dathostBudget.cost')}
            emptyText={t('admin.dathostBudget.noMatches')}
          />
        </Panel>

        <Panel title={t('admin.dathostBudget.matchesTitle')} wide>
          {matches.length === 0 ? (
            <p className="dhb-empty">{t('admin.dathostBudget.noMatches')}</p>
          ) : (
            <div className="dhb-table-wrap">
              <table className="dhb-table">
                <thead>
                  <tr>
                    <th>{t('admin.dathostBudget.colMatch')}</th>
                    <th>{t('admin.dathostBudget.colKind')}</th>
                    <th>{t('admin.dathostBudget.colStarted')}</th>
                    <th className="dhb-num">{t('admin.dathostBudget.colMinutes')}</th>
                    <th className="dhb-num">{t('admin.dathostBudget.cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => (
                    <tr key={m.match_id}>
                      <td>
                        <Link to={`/stats/matches/${m.match_id}`}>
                          #{m.match_id} {m.team1} – {m.team2}
                        </Link>
                      </td>
                      <td>{m.kind ? t(`admin.dathostBudget.kind.${m.kind}`) : '—'}</td>
                      <td>{formatDateTime(m.first_seen_at)}</td>
                      <td className="dhb-num">{m.minutes_on}</td>
                      <td className="dhb-num">{formatEur(m.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title={t('admin.dathostBudget.serversTitle')} wide>
          <div className="dhb-table-wrap">
            <table className="dhb-table">
              <thead>
                <tr>
                  <th>{t('admin.dathostBudget.colServer')}</th>
                  <th>{t('admin.dathostBudget.colState')}</th>
                  <th className="dhb-num">{t('admin.dathostBudget.colPerHour')}</th>
                  <th>{t('admin.dathostBudget.colAutostop')}</th>
                </tr>
              </thead>
              <tbody>
                {overview.servers.map((s) => (
                  <tr key={s.dathost_id}>
                    <td>{s.name}</td>
                    <td>
                      {s.on
                        ? t('admin.dathostBudget.onBecause', {
                          cause: t(`admin.dathostBudget.cause.${s.cause || 'unknown'}`),
                        })
                        : t('admin.dathostBudget.off')}
                    </td>
                    <td className="dhb-num">{formatEur(s.cost_per_hour)}</td>
                    <td>
                      {s.autostop ? (
                        <span className="dhb-badge dhb-badge-ok">
                          {t('admin.dathostBudget.autostopMinutes', { count: s.autostop_minutes })}
                        </span>
                      ) : (
                        <span className="dhb-badge dhb-badge-warn">
                          ⚠ {t('admin.dathostBudget.autostopOff')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title={t('admin.dathostBudget.invoicesTitle')} desc={t('admin.dathostBudget.invoicesDesc')} wide>
          {invoices?.error && <div className="admin-error">{invoices.error}</div>}
          {invoices && invoices.invoices.length > 0 && (
            <div className="dhb-table-wrap">
              <table className="dhb-table">
                <thead>
                  <tr>
                    <th>{t('admin.dathostBudget.colDate')}</th>
                    <th>{t('admin.dathostBudget.colType')}</th>
                    <th>{t('admin.dathostBudget.colDescription')}</th>
                    <th className="dhb-num">{t('admin.dathostBudget.colAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.invoices.map((i) => (
                    <tr key={i.id}>
                      <td>{formatDateTime(i.at)}</td>
                      <td>
                        {i.is_deposit
                          ? t('admin.dathostBudget.typeDeposit')
                          : t('admin.dathostBudget.typeSubscription')}
                        {i.refunded && ` (${t('admin.dathostBudget.refunded')})`}
                      </td>
                      <td>{i.description}</td>
                      <td className="dhb-num">{formatEur(i.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {form && (
          <Panel title={t('admin.dathostBudget.settingsTitle')} desc={t('admin.dathostBudget.settingsDesc')} wide>
            {saveError && <div className="admin-error">{saveError}</div>}
            {notice && <div className="admin-success">{notice}</div>}
            <div className="dhb-settings">
              {['enabled', 'alerts_enabled'].map((key) => (
                <div key={key} className="admin-row dhb-setting">
                  <span className="admin-label">{t(`admin.dathostBudget.fields.${key}`)}</span>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={!!form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                    />
                    <span className="admin-toggle-slider"></span>
                  </label>
                </div>
              ))}
              {NUMBER_FIELDS.map((f) => (
                <div key={f.key} className="admin-row dhb-setting">
                  <span className="admin-label">{t(`admin.dathostBudget.fields.${f.key}`)}</span>
                  <input
                    className="admin-input dhb-num-input"
                    type="number"
                    step={f.step}
                    min={f.min}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
              <div className="admin-row dhb-setting">
                <span className="admin-label">{t('admin.dathostBudget.fields.budget_alert_pcts')}</span>
                <input
                  className="admin-input dhb-num-input"
                  type="text"
                  value={form.budget_alert_pcts}
                  onChange={(e) => setForm({ ...form, budget_alert_pcts: e.target.value })}
                />
              </div>
              <div className="admin-row dhb-setting">
                <span className="admin-label">{t('admin.dathostBudget.fields.cost_source')}</span>
                <select
                  className="admin-input dhb-num-input"
                  value={form.cost_source}
                  onChange={(e) => setForm({ ...form, cost_source: e.target.value })}
                >
                  {['delta', 'estimate'].map((s) => (
                    <option key={s} value={s}>{t(`admin.dathostBudget.sources.${s}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="dhb-panel-desc">{t('admin.dathostBudget.alertsNote')}</p>
            <button className="admin-btn" onClick={handleSave} disabled={saving}>
              {saving ? t('admin.dathostBudget.saving') : t('admin.dathostBudget.save')}
            </button>
          </Panel>
        )}
      </div>
    </div>
  );
}
